import { Grid, Tabs, Typography } from '@mui/material';
import {
    AssetTypes,
    OrderSide,
    TransactionStatusCode,
    TransactionTypeChar,
    assetPurposeTypeCharMap
} from '@tassat/common-definitions';
import { Asset } from '@tassat/fred-definitions';
import { TransactionHistoryRecord } from '@tassat/fred-extended-definitions';
import { TabPanel } from '@tassat/layout';
import { StyledTab } from '@tassat/menu';
import { TransactionDetail } from '@tassat/text';
import { formatAsAsset, formatEpochDate } from '@tassat/utility';
import React from 'react';

import { getTransactionTypeString } from 'Lib/transactionTypeHelper';
import { ExecutionsTable } from 'SharedComponents/transactionDetails/transactionHeaderSectionDetails/executionsTable/executionsTable';

import { muiStyles } from './styles';
import { TransactionHeaderSectionDetailsProps, TransactionSectionDetailsProps } from './types';
import { FromFirmDetail, ToFirmDetail } from '../transactionFirmDetails/transactionFirmDetails';

const TransactionSectionDetails = (props: TransactionSectionDetailsProps) => {
    const {
        selectedTransaction,
        currentUser,
        declineReason,
        orderExecutions,
        withdrawalData
    } = props;

    const classes = muiStyles();

    return (
        <Grid
            className={classes.detailSectionContainer}
            container
            direction='row'
            item
            justifyContent='space-around'
            spacing={0}
            wrap='wrap'
            xs={12}
        >
            <FromFirmDetail
                currentUser={currentUser}
                selectedTransaction={selectedTransaction}
            />
            <ToFirmDetail
                currentUser={currentUser}
                selectedTransaction={selectedTransaction}
                withdrawalData={withdrawalData}
            />
            <Grid
                className={classes.detailSection}
                container
                direction='column'
                item
                spacing={0}
                wrap='nowrap'
            >
                <TransactionDetail
                    attribute={selectedTransaction.APIRefId || '-'}
                    id='transactionDetails_transactionApiReference'
                    label='API Tran Ref'
                    orientation='column'
                />
                <TransactionDetail
                    attribute={selectedTransaction.APIConnectionName || '-'}
                    id='transactionDetails_transactionApiName'
                    label='API Name'
                    orientation='column'
                />
                {(
                    selectedTransaction.type !== TransactionTypeChar.order
                ) &&
                (<TransactionDetail
                    attribute={selectedTransaction.PublicDescription || '-'}
                    id='transactionDetails_publicDescription'
                    label='Public Description'
                    orientation='column'
                />)}
                <TransactionDetail
                    attribute={selectedTransaction.PrivateDescription || '-'}
                    id='transactionDetails_privateDescription'
                    label='Private Description'
                    orientation='column'
                />
                {(
                    selectedTransaction.type === TransactionTypeChar.order
                ) &&
                (<TransactionDetail
                    attribute={formatAsAsset(selectedTransaction.FeeAmount) || '-'}
                    id='transactionDetails_fee'
                    label='Fees'
                    orientation='column'
                />)}
                {
                    selectedTransaction.type === TransactionTypeChar.order ?
                        (<TransactionDetail
                            attribute={formatEpochDate(selectedTransaction.RequestTime) || '-'}
                            id='transactionDetails_transactionRequestTime'
                            label='Request Time'
                            orientation='column'
                        />) : null
                }
                {declineReason && (
                    <TransactionDetail
                        attribute={declineReason}
                        id='transactionDetails_declineReason'
                        label='Decline Reason'
                        orientation='column'
                    />
                )}
                {(
                    selectedTransaction.type === TransactionTypeChar.send ||
                    selectedTransaction.type === TransactionTypeChar.transfer
                ) &&
                (
                    <TransactionDetail
                        attribute={assetPurposeTypeCharMap(selectedTransaction.Purpose)}
                        id='transactionDetails_purpose'
                        label='Purpose'
                        orientation='column'
                    />
                )}
                {(
                    (selectedTransaction.type === TransactionTypeChar.send ||
                        selectedTransaction.type === TransactionTypeChar.transfer) && selectedTransaction.PledgeId !== '0'
                ) &&
                (
                    <TransactionDetail
                        attribute={selectedTransaction.PledgeId}
                        id='transactionDetails_pledgeId'
                        label='Post ID'
                        orientation='column'
                    />
                )}
            </Grid>
        </Grid>
    );
};

export const TransactionHeaderSectionDetails = (props: TransactionHeaderSectionDetailsProps) => {
    const {
        isSecurityAssetTxn,
        selectedTransaction,
        currentUser,
        declineReason,
        asset,
        currency,
        orderExecutions,
        withdrawalData
    } = props;

    const classes = muiStyles();
    const [ currentTab, setCurrentTab ] = React.useState<number>(0);
    const hidePendingWord = (
        selectedTransaction.Status === TransactionStatusCode.CANCELED ||
        selectedTransaction.Status === TransactionStatusCode.DECLINED ||
        selectedTransaction.Status === TransactionStatusCode.FAILED ||
        selectedTransaction.Status === TransactionStatusCode.REJECTED
    ) && (
        selectedTransaction.type === TransactionTypeChar.send ||
        selectedTransaction.type === TransactionTypeChar.withdrawal ||
        selectedTransaction.type === TransactionTypeChar.deposit
    ) || (selectedTransaction.type === TransactionTypeChar.order && (
        selectedTransaction.OrderSide === OrderSide.REDEEM ||
        selectedTransaction.OrderSide === OrderSide.SUBSCRIPTION
    )) || ((selectedTransaction.type === TransactionTypeChar.send ||
        selectedTransaction.type === TransactionTypeChar.transfer) &&
        selectedTransaction.FiatAmount == '0');

    const hideConfirmationId = (
        selectedTransaction.type === TransactionTypeChar.order ||
        selectedTransaction.type === TransactionTypeChar.deposit ||
        selectedTransaction.type === TransactionTypeChar.withdrawal ||
        selectedTransaction.type === TransactionTypeChar.int_distribution
    );
    const completedIntDist = (
        selectedTransaction.type === TransactionTypeChar.int_distribution && selectedTransaction.IsCompleted
    );

    const renderAmount = (selectedTransaction: TransactionHistoryRecord, asset: Asset, showSecurityAmount: boolean) => {
        if (showSecurityAmount) {
            return selectedTransaction.AssetQuantity || selectedTransaction.AssetQuantity.length ?
                formatAsAsset(selectedTransaction.AssetQuantity, asset, 'ticker', true, true)
                : `${asset.Ticker}: ${hidePendingWord ? '-' : 'Pending'}`;
        }

        const currencyAmount = ((asset.Type === AssetTypes.CURRENCY &&
            (selectedTransaction.type === TransactionTypeChar.deposit ||
                selectedTransaction.type === TransactionTypeChar.send ||
                selectedTransaction.type === TransactionTypeChar.transfer ||
                selectedTransaction.type === TransactionTypeChar.withdrawal ||
                selectedTransaction.type === TransactionTypeChar.receive)
        ) ? selectedTransaction.AssetQuantity : selectedTransaction.FiatAmount).toString();

        return currencyAmount || currencyAmount.length ?
            formatAsAsset(currencyAmount, asset, 'curcode', true)
            : `${asset.Ticker}: ${hidePendingWord ? '-' : 'Pending'}`;
    };

    const SectionDetails = React.useMemo(() => (
        <>
            <Grid
                className={classes.headerAmountDetailsMain}
                container
                direction='row'
                item
                spacing={0}
                wrap='wrap'
                xs={12}
            >
                <Grid
                    item
                    xs={5}
                >
                    <Typography
                        className={classes.transactionAmount}
                        id='transactionDetails_transactionAmount_value'
                        variant='h3'
                    >
                        <strong>
                            {renderAmount(selectedTransaction, asset, isSecurityAssetTxn)}
                        </strong>
                        {isSecurityAssetTxn &&
                            (
                            <Typography
                                className={classes.transactionAmount}
                                variant='body1'
                            >
                                {
                                    selectedTransaction.FiatAmount || completedIntDist ?
                                        formatAsAsset(selectedTransaction.FiatAmount, {
                                            ...asset,
                                            DecimalPoint: 2
                                        }, 'curcode', true)
                                        : `${asset.Curcode}: ${hidePendingWord ? '-' : 'Pending'}`
                                }
                            </Typography>)}
                    </Typography>
                </Grid>
                <Grid
                    className={classes.headerDetailsContainer}
                    container
                    direction='column'
                    item
                    spacing={0}
                    xs={7}
                >
                    <Grid
                        container
                        direction='row'
                        item
                        spacing={0}
                        wrap='wrap'
                    >
                        <Grid
                            className={classes.headerSmallDetail}
                            container
                            direction='row'
                            item
                            spacing={0}
                            wrap='nowrap'
                        >
                            <Typography
                                className={classes.headerLabel}
                                id='transactionDetails_transactionType_label'
                                variant='body2'
                            >
                                Type:
                            </Typography>
                            <Typography
                                className={classes.headerAttribute}
                                id='transactionDetails_transactionType_value'
                                variant='body1'
                            >
                                {getTransactionTypeString(selectedTransaction)}
                            </Typography>
                        </Grid>
                        <Grid
                            className={classes.headerSmallDetail}
                            container
                            direction='row'
                            item
                            spacing={0}
                            wrap='nowrap'
                        >
                            <Typography
                                className={classes.headerLabel}
                                id='transactionDetails_transactionStatus_label'
                                variant='body2'
                            >
                                Status:
                            </Typography>
                            <Typography
                                className={classes.headerAttribute}
                                id='transactionDetails_transactionStatus_value'
                                variant='body1'
                            >
                                {selectedTransaction.UIStatus}
                            </Typography>
                        </Grid>
                        {(
                            selectedTransaction.type === TransactionTypeChar.order &&
                            <>
                                <>
                                    <Grid
                                        className={classes.headerSmallDetail}
                                        container
                                        direction='row'
                                        item
                                        spacing={0}
                                        wrap='nowrap'
                                    >
                                        <Typography
                                            className={classes.headerLabel}
                                            id='transactionDetails_cumulativeQuantity_label'
                                            variant='body2'
                                        >
                                            Cumulative Quantity:
                                        </Typography>
                                        <Typography
                                            className={classes.headerAttribute}
                                            id='transactionDetails_cumulativeQuantity_value'
                                            variant='body1'
                                        >
                                            {orderExecutions?.executionList[0]?.CumQty}
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        className={classes.headerSmallDetail}
                                        container
                                        direction='row'
                                        item
                                        spacing={0}
                                        wrap='nowrap'
                                    >
                                        <Typography
                                            className={classes.headerLabel}
                                            id='transactionDetails_leavesQuantity_label'
                                            variant='body2'
                                        >
                                            Leaves Quantity:
                                        </Typography>
                                        <Typography
                                            className={classes.headerAttribute}
                                            id='transactionDetails_leavesQuantity_value'
                                            variant='body1'
                                        >
                                            {orderExecutions?.executionList[0]?.LeavesQty}
                                        </Typography>
                                    </Grid>

                                </>
                                <Grid
                                    className={classes.headerSmallDetail}
                                    container
                                    direction='row'
                                    item
                                    spacing={0}
                                    wrap='nowrap'
                                >
                                    <Typography
                                        className={classes.headerLabel}
                                        id='transactionDetails_leavesQuantity_label'
                                        variant='body2'
                                    >
                                        Order ID:
                                    </Typography>
                                    <Typography
                                        className={classes.headerAttribute}
                                        id='transactionDetails_leavesQuantity_value'
                                        variant='body1'
                                    >
                                        {orderExecutions?.orderId}
                                    </Typography>
                                </Grid>
                            </>
                        )}
                        {(
                            (
                                (
                                    selectedTransaction.type === TransactionTypeChar.send ||
                                    selectedTransaction.type === TransactionTypeChar.receive ||
                                    selectedTransaction.type === TransactionTypeChar.transfer
                                ) && (asset.Type === AssetTypes.ASSET)) ||
                                selectedTransaction.type === TransactionTypeChar.int_distribution ||
                                (
                                    selectedTransaction.type === TransactionTypeChar.order &&
                                    (
                                        selectedTransaction.OrderSide === OrderSide.SELL ||
                                        selectedTransaction.OrderSide === OrderSide.BUY
                                    )
                                )

                        ) &&
                        (
                            <Grid
                                className={classes.headerSmallDetail}
                                container
                                direction='row'
                                item
                                spacing={0}
                                wrap='nowrap'
                            >
                                <Typography
                                    className={classes.headerLabel}
                                    id='transactionDetails_price_label'
                                    variant='body2'
                                >
                                    Price:
                                </Typography>
                                <Typography
                                    className={classes.headerAttribute}
                                    id='transactionDetails_price_value'
                                    variant='body1'
                                >
                                    {formatAsAsset(selectedTransaction.RequestPrice)}
                                </Typography>
                            </Grid>
                        )}
                        {(selectedTransaction.type === TransactionTypeChar.transfer ||
                            selectedTransaction.type === TransactionTypeChar.send) &&
                            <>
                                <Grid
                                    className={classes.headerSmallDetail}
                                    container
                                    direction='row'
                                    item
                                    spacing={0}
                                    wrap='nowrap'
                                >
                                    <Typography
                                        className={classes.headerLabel}
                                        id='transactionDetails_cost_basis_label'
                                        variant='body2'
                                    >
                                        Cost Basis:
                                    </Typography>
                                    <Typography
                                        className={classes.headerAttribute}
                                        id='transactionDetails_cost_basis_value'
                                        variant='body1'
                                    >
                                        {selectedTransaction.CostBasis ? formatAsAsset(selectedTransaction.CostBasis) : '-'}
                                    </Typography>
                                </Grid>
                                <Grid
                                    className={classes.headerSmallDetail}
                                    container
                                    direction='row'
                                    item
                                    spacing={0}
                                    wrap='nowrap'
                                >
                                    <Typography
                                        className={classes.headerLabel}
                                        id='transactionDetails_total_cost_label'
                                        variant='body2'
                                    >
                                        Total Cost:
                                    </Typography>
                                    <Typography
                                        className={classes.headerAttribute}
                                        id='transactionDetails_total_cost_value'
                                        variant='body1'
                                    >
                                        {selectedTransaction.TotalCost ? formatAsAsset(selectedTransaction.TotalCost, currency) : '-'}
                                    </Typography>
                                </Grid>
                                <Grid
                                    className={classes.headerSmallDetail}
                                    container
                                    direction='row'
                                    item
                                    spacing={0}
                                    wrap='nowrap'
                                >
                                    <Typography
                                        className={classes.headerLabel}
                                        id='transactionDetails_total_proceeds_label'
                                        variant='body2'
                                    >
                                        Total Proceeds:
                                    </Typography>
                                    <Typography
                                        className={classes.headerAttribute}
                                        id='transactionDetails_total_proceeds_value'
                                        variant='body1'
                                    >
                                        {selectedTransaction.TotalProceeds ? formatAsAsset(selectedTransaction.TotalProceeds, currency) : '-'}
                                    </Typography>
                                </Grid>
                            </>}
                    </Grid>
                    {!hideConfirmationId &&
                        (
                        <Grid
                            className={classes.headerSmallDetail}
                            container
                            direction='row'
                            item
                            spacing={0}
                            wrap='nowrap'
                        >
                            <Typography
                                className={classes.headerLabel}
                                id='transactionDetails_confirmationId_label'
                                variant='body2'
                            >
                                Confirmation ID:
                            </Typography>
                            <Typography
                                className={`${classes.headerAttribute} ${classes.headerAttributeNoCaps}`}
                                id='transactionDetails_confirmationId_value'
                                variant='body1'
                            >
                                {selectedTransaction.ConfirmationId}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Grid>
            <TransactionSectionDetails
                currentUser={currentUser}
                declineReason={declineReason}
                orderExecutions={orderExecutions}
                selectedTransaction={selectedTransaction}
                withdrawalData={withdrawalData}
            />
        </>
    ), [ selectedTransaction, orderExecutions ]);

    if (selectedTransaction.type === TransactionTypeChar.order) {
        return (
            <Grid
                container
                justifyContent='center'
            >
                <Grid
                    className={classes.mainTabContainer}
                    item
                    xs={9}
                >
                    <Tabs
                        centered
                        classes={{ indicator: classes.indicator }}
                        className={classes.userTabsContainer}
                        onChange={(e, newTab) => setCurrentTab(newTab)}
                        value={currentTab}
                    >
                        <StyledTab
                            id='transactionDetailsTab'
                            label='Details'
                            tabClasses={{
                                root: {
                                    fontSize: '0.815rem',
                                    minHeight: '0rem',
                                    height: '100%',
                                    width: '100%',
                                    backgroundColor: '#fff',
                                    opacity: '1'
                                }
                            }}
                        />
                        <StyledTab
                            id='transactionExecutionsTab'
                            label='Executions'
                            tabClasses={{
                                root: {
                                    fontSize: '0.815rem',
                                    minHeight: '0rem',
                                    height: '100%',
                                    width: '100%',
                                    backgroundColor: '#fff',
                                    opacity: '1'
                                }
                            }}
                        />
                    </Tabs>
                </Grid>
                <TabPanel
                    index={0}
                    value={currentTab}
                >
                    {SectionDetails}
                </TabPanel>
                <TabPanel
                    index={1}
                    value={currentTab}
                >
                    <Grid
                        container
                        item
                        justifyContent='center'
                        xs={12}
                    >
                        <ExecutionsTable
                            asset={asset}
                            executionList={orderExecutions.executionList}
                            transaction={selectedTransaction?.OrderSide}
                        />
                    </Grid>
                </TabPanel>
            </Grid>
        );
    }

    return SectionDetails;
};